/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* eslint-disable no-duplicate-imports */
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import LinksComponent from './components/links';
import { actionableRegion as region, IContentBlockNoHeadingsProps } from './content-block-no-headings.props.autogenerated';

export interface IContentBlockViewProps extends IContentBlockNoHeadingsProps<{}> {
  title?: React.ReactNode;
  text?: React.ReactNode;
  image?: React.ReactNode;
  links?: React.ReactNode;
  className?: string;
  contentBlockContainer: IModuleProps;
  imageContainer: INodeProps;
  detailsContainer: INodeProps;
  contentBlockAnchorTag?: INodeProps;
  imageLink?: string;
  imageAriaLabel?: string;
}
/**
 *
 * ContentCard component
 * @extends {React.PureComponent<IContentBlockNoHeadingsProps{}>}
 */

class ContentBlock extends React.PureComponent<IContentBlockNoHeadingsProps<{}>> {
  private telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
    this.props.friendlyName,
    this.props.telemetry);
  public render(): JSX.Element | null {
    const {
      heading,
      image,
      links,
      className,
      imageAriaLabel
    } = this.props.config;
    let { paragraph } = this.props.config;
    const contentBlockTitle = heading && (
      <Msdyn365.Text
        className='ms-content-block__title'
        tag={heading.tag || 'h1'}
        text={heading.text}
        editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
      />
    );
    const imageProps = {
      gridSettings: this.props.context.request.gridSettings || {},
      imageSettings: image && image.imageSettings,
    };
    const contentBlockLinks = links && links.length > 0 && (
      <LinksComponent {...{ links: links, onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request, telemetryContent: this.telemetryContent }} />
    );
    //Change the paragraph source by replacing all the heading tags to p tags and adding custom styling for font size to them
    if (paragraph) {
      if (paragraph.indexOf("<h1") >= 0 && paragraph.indexOf("</h1>") >= 0) {
        const modifyh1 = paragraph.substring(paragraph.indexOf("<h1"), paragraph.indexOf("</h1>") + 5);
        let updatedh1 = modifyh1.replace("<h1", "<div").replace("</h1>", "</div>").replace('class="', 'class="h1 ');
        if (!updatedh1.includes("class")) {
          updatedh1 = updatedh1.replace("<div", "<div class='h1'");
        }
        paragraph = paragraph.replace(modifyh1, updatedh1);
      }
      if (paragraph.indexOf("<h2") >= 0 && paragraph.indexOf("</h2>") >= 0) {
        const modifyh2 = paragraph.substring(paragraph.indexOf("<h2"), paragraph.indexOf("</h2>") + 5);
        let updatedh2 = modifyh2.replace("<h2", "<div").replace("</h2>", "</div>").replace('class="', 'class="h2 ');
        if (!updatedh2.includes("class")) {
          updatedh2 = updatedh2.replace("<div", "<div class='h2'");
        }
        paragraph = paragraph.replace(modifyh2, updatedh2);
      }
      if (paragraph.indexOf("<h3") >= 0 && paragraph.indexOf("</h3>") >= 0) {
        const modifyh3 = paragraph.substring(paragraph.indexOf("<h3"), paragraph.indexOf("</h3>") + 5);
        let updatedh3 = modifyh3.replace("<h3", "<div").replace("</h3>", "</div>").replace('class="', 'class="h3 ');
        if (!updatedh3.includes("class")) {
          updatedh3 = updatedh3.replace("<div", "<div class='h3'");
        }
        paragraph = paragraph.replace(modifyh3, updatedh3);
      }
      if (paragraph.indexOf("<h4") >= 0 && paragraph.indexOf("</h4>") >= 0) {
        const modifyh4 = paragraph.substring(paragraph.indexOf("<h4"), paragraph.indexOf("</h4>") + 5);
        let updatedh4 = modifyh4.replace("<h4", "<div").replace("</h4>", "</div>").replace('class="', 'class="h4 ');
        if (!updatedh4.includes("class")) {
          updatedh4 = updatedh4.replace("<div", "<div class='h4'");
        }
        paragraph = paragraph.replace(modifyh4, updatedh4);
      }
      if (paragraph.indexOf("<h5") >= 0 && paragraph.indexOf("</h5>") >= 0) {
        const modifyh5 = paragraph.substring(paragraph.indexOf("<h5"), paragraph.indexOf("</h5>") + 5);
        let updatedh5 = modifyh5.replace("<h5", "<div").replace("</h5>", "</div>").replace('class="', 'class="h5 ');
        if (!updatedh5.includes("class")) {
          updatedh5 = updatedh5.replace("<div", "<div class='h5'");
        }
        paragraph = paragraph.replace(modifyh5, updatedh5);
      }
      if (paragraph.indexOf("<h6") >= 0 && paragraph.indexOf("</h6>") >= 0) {
        const modifyh6 = paragraph.substring(paragraph.indexOf("<h6"), paragraph.indexOf("</h6>") + 5);
        let updatedh6 = modifyh6.replace("<h6", "<div").replace("</h6>", "</div>").replace('class="', 'class="h6 ');
        if (!updatedh6.includes("class")) {
          updatedh6 = updatedh6.replace("<div", "<div class='h6'");
        }
        paragraph = paragraph.replace(modifyh6, updatedh6);
      }
    }
    const contentBlockText = paragraph && (
      <Msdyn365.RichTextComponent
        text={paragraph}
        className='ms-content-block__text'
        editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
      />
    );
    const contentBlockImage = image && (
      <Msdyn365.Image {...image} {...imageProps} editProps={{ key: this.props.config.image || {}, requestContext: this.props.context.request }} />
    );

    if (
      !contentBlockTitle &&
      !contentBlockText &&
      !contentBlockImage &&
      !contentBlockLinks
    ) {
      this.props.context.telemetry.error(
        'Content block content is empty, module wont render.'
      );
      return null;
    }

    const contentBlockviewProps = {
      ...this.props,
      title: contentBlockTitle,
      text: contentBlockText,
      image: contentBlockImage,
      links: contentBlockLinks,
      moduleClass: this.props.config.className,
      contentBlockContainer: {
        moduleProps: this.props,
        className: classnames('ms-content-block', className),
      },
      imageContainer: { className: 'ms-content-block__image' },
      detailsContainer: { className: 'ms-content-block__details' },
      contentBlockAnchorTag: {
        tag: 'a',
        className: 'ms-content-block__link',
        role: 'link',
      },
      imageLink: this._getImageLink(),
      imageAriaLabel
    };

    return this.props.renderView(contentBlockviewProps) as React.ReactElement;
  }
  public handleTextChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.heading!.text = event.target.value;
  public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.paragraph = event.target.value;
  /* Handle link text change*/
  public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
    if (this.props.config.links && this.props.config.links[linkIndex]) {
      this.props.config.links[linkIndex].linkText = event.target.value;
    }
  };
  private _getImageLink(): string | null {
    const { imageLink, links, actionableRegion } = this.props.config;
    if (actionableRegion === region.imageAndLinks) {
      if (imageLink && imageLink.destinationUrl) {
        return imageLink.destinationUrl;
      } else if (links && links.length && links[0].linkUrl) {
        return links[0].linkUrl.destinationUrl;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
}

export default ContentBlock;
